import { Shield, Lock, Settings, Database } from 'lucide-react';

import Docker from '@/assets/ico/vendor/docker.svg?c';
import Podman from '@/assets/ico/vendor/podman.svg?c';
import Kubernetes from '@/assets/ico/vendor/kubernetes.svg?c';
import type { Environment } from '@/react/portainer/environments/types';
import type { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';

import { BoxSelector, BoxSelectorOption } from '@@/BoxSelector';
import { LoadingButton } from '@@/buttons';
import { Badge } from '@@/Badge';
import { FormSectionTitle } from '@@/form-components/FormSectionTitle';
import { Link } from '@@/Link';

import { PolicyType, PolicyCategory, PolicyEnvironmentType } from '../../types';
import {
  getVisiblePolicyTypes,
  getCompatibleGroupsCount,
  getSupportedEnvironmentTypes,
} from '../../utils/policyTypeUtils';
import { PolicyTypeMetadata } from '../../utils/policy-types-metadata';

import { PolicyTypeToolbar } from './PolicyTypeToolbar';

type EnvironmentTypeFilter =
  | 'all'
  | 'kubernetes'
  | 'docker'
  | 'podman'
  | 'swarm';
type CategoryFilter = 'all' | PolicyCategory;

interface Props {
  value: PolicyType | null;
  onChange: (policyType: PolicyType) => void;
  onContinue: () => void;
  environments: Environment[];
  groups: EnvironmentGroup[];
  environmentFilter: EnvironmentTypeFilter;
  onEnvironmentFilterChange: (filter: EnvironmentTypeFilter) => void;
  categoryFilter: CategoryFilter;
  onCategoryFilterChange: (filter: CategoryFilter) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
}

export function PolicyTypeSelector({
  value,
  onChange,
  onContinue,
  environments,
  groups,
  environmentFilter,
  onEnvironmentFilterChange,
  categoryFilter,
  onCategoryFilterChange,
  searchQuery,
  onSearchQueryChange,
}: Props) {
  // Get only policy types that have compatible groups (filters out those with 0 groups)
  const visiblePolicyTypes = getVisiblePolicyTypes(environments, groups);

  // Filter policy types based on selected environment type, category, and search
  const filteredPolicyTypes = filterPolicyTypes(
    visiblePolicyTypes,
    environmentFilter,
    categoryFilter,
    searchQuery
  );

  // Build options for BoxSelector
  const allOptions: BoxSelectorOption<PolicyType>[] = filteredPolicyTypes.map(
    (metadata) => buildPolicyTypeOption(metadata, environments, groups)
  );

  const hasNoPolicyTypes = allOptions.length === 0;

  const availableEnvTypes = getAvailableEnvironmentTypes(visiblePolicyTypes);
  const availableCategories = getAvailableCategories(visiblePolicyTypes);

  const environmentFilterOptions =
    buildEnvironmentFilterOptions(availableEnvTypes);
  const categoryFilterOptions = buildCategoryFilterOptions(availableCategories);

  return (
    <div>
      <FormSectionTitle>Policy Type</FormSectionTitle>
      <PolicyTypeToolbar
        searchQuery={searchQuery}
        onSearchQueryChange={onSearchQueryChange}
        environmentFilter={environmentFilter}
        environmentFilterOptions={environmentFilterOptions}
        onEnvironmentFilterChange={onEnvironmentFilterChange}
        categoryFilter={categoryFilter}
        categoryFilterOptions={categoryFilterOptions}
        onCategoryFilterChange={onCategoryFilterChange}
      />
      {hasNoPolicyTypes ? (
        <NoCompatibleGroupsAlert envTypeSelected={environmentFilter} />
      ) : (
        <div>
          <BoxSelector<PolicyType>
            radioName="policy-type"
            value={value || ('' as PolicyType)}
            onChange={(selectedType) => onChange(selectedType)}
            options={allOptions}
          />

          <LoadingButton
            disabled={!value}
            onClick={onContinue}
            isLoading={false}
            loadingText="Loading"
            data-cy="policy-type-continue-button"
          >
            Continue
          </LoadingButton>
        </div>
      )}
    </div>
  );
}

/**
 * Build a BoxSelectorOption for a policy type
 */
function buildPolicyTypeOption(
  metadata: PolicyTypeMetadata,
  environments: Environment[],
  groups: EnvironmentGroup[]
): BoxSelectorOption<PolicyType> {
  const compatibleGroupsCount = getCompatibleGroupsCount(
    metadata.id,
    environments,
    groups
  );

  const supportedEnvTypes = getSupportedEnvironmentTypes(metadata.id);
  const PolicyIcon = getPolicyIcon(metadata.category);

  return {
    id: metadata.id,
    value: metadata.id,
    label: metadata.displayName,
    icon: PolicyIcon,
    description: (
      <div className="flex flex-col gap-4">
        {/* Description */}
        <div className="text-xs text-muted">{metadata.description}</div>

        {/* Environment type badges with label */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs">Compatible with:</span>
          {supportedEnvTypes.map((envType) => {
            const EnvironmentIcon = getEnvironmentTypeIcon(envType);
            return (
              <Badge key={envType} type="muted" className="min-w-max">
                <EnvironmentIcon className="lucide-icon mr-1 !h-4 !w-4" />
                {getEnvironmentTypeDisplayName(envType)}
              </Badge>
            );
          })}
        </div>

        {/* Groups available text */}
        <div className="text-xs flex flex-wrap items-center gap-x-2">
          <span>Available in:</span>
          <span className="text-muted">
            {' '}
            {compatibleGroupsCount} environment{' '}
            {compatibleGroupsCount === 1 ? 'group' : 'groups'}
          </span>
        </div>
      </div>
    ),
  };
}

function NoCompatibleGroupsAlert({
  envTypeSelected,
}: {
  envTypeSelected: EnvironmentTypeFilter;
}) {
  if (envTypeSelected === 'all') {
    return (
      <div className="text-sm text-muted">
        No compatible environment groups available. Create{' '}
        <Link to="portainer.groups.new" data-cy="environments-view-link">
          environment groups
        </Link>{' '}
        with Docker, Podman, or Swarm agents, or Kubernetes edge agents to use
        this feature.
      </div>
    );
  }

  if (envTypeSelected === 'kubernetes') {
    return (
      <div className="text-sm text-muted">
        No compatible environment groups available. Create{' '}
        <Link to="portainer.groups.new" data-cy="environments-view-link">
          environment groups
        </Link>{' '}
        with Kubernetes edge agents to use this feature.
      </div>
    );
  }

  return (
    <div className="text-sm text-muted">
      No compatible environment groups available. Create{' '}
      <Link to="portainer.groups.new" data-cy="environments-view-link">
        environment groups
      </Link>{' '}
      with Docker, Podman, or Swarm agents to use this feature.
    </div>
  );
}

/**
 * Filter policy types based on environment type, category, and search query
 */
function filterPolicyTypes(
  policyTypes: PolicyTypeMetadata[],
  environmentFilter: EnvironmentTypeFilter,
  categoryFilter: CategoryFilter,
  searchQuery: string
): PolicyTypeMetadata[] {
  return policyTypes.filter((metadata) => {
    // Filter by environment type
    if (environmentFilter !== 'all') {
      const supportedEnvTypes = getSupportedEnvironmentTypes(metadata.id);
      if (!supportedEnvTypes.includes(environmentFilter)) {
        return false;
      }
    }

    // Filter by category
    if (categoryFilter !== 'all' && metadata.category !== categoryFilter) {
      return false;
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = metadata.displayName.toLowerCase().includes(query);
      const matchesDescription = metadata.description
        .toLowerCase()
        .includes(query);
      if (!matchesName && !matchesDescription) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Get available environment types from visible policy types
 */
function getAvailableEnvironmentTypes(
  policyTypes: PolicyTypeMetadata[]
): Set<PolicyEnvironmentType> {
  const availableEnvTypes = new Set<PolicyEnvironmentType>();
  policyTypes.forEach((metadata) => {
    const supportedTypes = getSupportedEnvironmentTypes(metadata.id);
    supportedTypes.forEach((type) => availableEnvTypes.add(type));
  });
  return availableEnvTypes;
}

/**
 * Get available categories from visible policy types
 */
function getAvailableCategories(
  policyTypes: PolicyTypeMetadata[]
): Set<PolicyCategory> {
  const availableCategories = new Set<PolicyCategory>();
  policyTypes.forEach((metadata) => {
    availableCategories.add(metadata.category);
  });
  return availableCategories;
}

/**
 * Build environment filter options
 */
function buildEnvironmentFilterOptions(
  availableEnvTypes: Set<PolicyEnvironmentType>
): Array<{ value: EnvironmentTypeFilter; label: string }> {
  const environmentTypeLabels: Record<PolicyEnvironmentType, string> = {
    kubernetes: 'Kubernetes',
    docker: 'Docker',
    podman: 'Podman',
    swarm: 'Swarm',
  };

  return [
    { value: 'all', label: 'All Environment Types' },
    ...Array.from(availableEnvTypes).map((envType) => ({
      value: envType,
      label: environmentTypeLabels[envType],
    })),
  ];
}

/**
 * Build category filter options
 */
function buildCategoryFilterOptions(
  availableCategories: Set<PolicyCategory>
): Array<{ value: CategoryFilter; label: string }> {
  const categoryLabels: Record<PolicyCategory, string> = {
    rbac: 'RBAC',
    security: 'Security',
    setup: 'Setup',
    registry: 'Registry',
  };

  return [
    { value: 'all', label: 'All Categories' },
    ...Array.from(availableCategories).map((category) => ({
      value: category,
      label: categoryLabels[category],
    })),
  ];
}

/**
 * Get display name for environment type
 */
function getEnvironmentTypeDisplayName(envType: string): string {
  switch (envType) {
    case 'kubernetes':
      return 'Kubernetes';
    case 'docker':
      return 'Docker';
    case 'podman':
      return 'Podman';
    case 'swarm':
      return 'Swarm';
    default:
      return envType;
  }
}

/**
 * Get icon for policy category
 */
function getPolicyIcon(category: PolicyCategory) {
  switch (category) {
    case 'rbac':
      return Lock;
    case 'security':
      return Shield;
    case 'setup':
      return Settings;
    case 'registry':
      return Database;
    default:
      return Shield;
  }
}

/**
 * Get icon for environment type
 */
function getEnvironmentTypeIcon(envType: string) {
  switch (envType) {
    case 'kubernetes':
      return Kubernetes;
    case 'podman':
      return Podman;
    case 'docker':
    case 'swarm':
    default:
      return Docker;
  }
}
